/**
 * Simple markdown parser for article summaries.
 *
 * This utility converts basic markdown formatting used in summaries
 * to properly formatted HTML for better readability.
 */

import React from 'react';

/**
 * Convert markdown text to plain text (strip formatting)
 * @param {string} text - Markdown text to convert
 * @returns {string} Plain text without markdown formatting
 */
export function markdownToPlainText(text) {
  if (!text) return '';

  return text
    // Remove headers (# ## ###)
    .replace(/^#{1,6}\s+/gm, '')
    // Remove bold/italic (**text** or *text* or __text__ or _text_)
    .replace(/(\*\*|__)(.*?)\1/g, '$2')
    .replace(/(\*|_)(.*?)\1/g, '$2')
    // Remove images ![alt](url)
    .replace(/!\[.*?\]\(.*?\)/g, '')
    // Remove links [text](url) - keep just the text
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    // Remove inline code `code`
    .replace(/`([^`]+)`/g, '$1')
    // Remove horizontal rules
    .replace(/^[-*_]{3,}\s*$/gm, '')
    // Normalize whitespace
    .replace(/\n\s*\n/g, '\n\n')
    .trim();
}

/**
 * Convert markdown text to formatted JSX
 * @param {string} text - Markdown text to convert
 * @returns {JSX.Element[]} Array of JSX elements
 */
export function parseMarkdownSummary(text) {
  if (!text) return [];

  const lines = text.split('\n');
  const elements = [];
  let key = 0;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]; // Don't trim yet - we need to preserve indentation
    
    if (!line.trim()) {
      // Empty line - add spacing
      elements.push(<div key={key++} className="h-3" />);
      continue;
    }

    // Check for section headers (these should not be indented)
    const trimmedLine = line.trim();

    // Check for standard markdown headers (# ## ###)
    const headerMatch = trimmedLine.match(/^(#{1,3})\s+(.*)$/);
    if (headerMatch) {
      const [, hashes, headerText] = headerMatch;
      const level = hashes.length;

      // Map header levels to appropriate styles
      let headerElement;
      if (level === 1) {
        headerElement = (
          <h1 key={key++} className="text-2xl font-bold text-gray-900 mt-8 mb-4 first:mt-0">
            {headerText}
          </h1>
        );
      } else if (level === 2) {
        headerElement = (
          <h2 key={key++} className="text-xl font-semibold text-gray-900 mt-6 mb-3 first:mt-0">
            {headerText}
          </h2>
        );
      } else { // level === 3
        headerElement = (
          <h3 key={key++} className="text-lg font-semibold text-gray-900 mt-6 mb-3 first:mt-0">
            {headerText}
          </h3>
        );
      }

      elements.push(headerElement);
      continue;
    }

    if (trimmedLine.startsWith('**') && trimmedLine.endsWith(':**')) {
      // Section headers like **Key Arguments:**
      const headerText = trimmedLine.replace(/^\*\*/, '').replace(/:\*\*$/, '');
      elements.push(
        <h3 key={key++} className="text-lg font-semibold text-gray-900 mt-6 mb-3 first:mt-0">
          {headerText}
        </h3>
      );
      continue;
    }

    // Check for markdown images (![alt](url))
    const imageMatch = trimmedLine.match(/!\[(.*?)\]\((.*?)\)/);
    if (imageMatch) {
      const [fullMatch, alt, url] = imageMatch;
      const imageIndex = trimmedLine.indexOf(fullMatch);

      // Extract text before and after image
      const textBefore = trimmedLine.substring(0, imageIndex).trim();
      const textAfter = trimmedLine.substring(imageIndex + fullMatch.length).trim();

      // Add text before image if exists
      if (textBefore) {
        const formattedText = formatInlineMarkdown(textBefore);
        elements.push(
          <p key={key++} className="text-gray-700 mb-3 leading-relaxed">
            {formattedText}
          </p>
        );
      }

      // Add image
      elements.push(
        <div key={key++} className="my-6">
          <img
            src={url}
            alt={alt || 'Image'}
            className="max-w-full h-auto rounded-lg shadow-sm"
            loading="lazy"
            referrerPolicy="no-referrer"
          />
          {alt && (
            <p className="text-sm text-gray-500 italic mt-2 text-center">{alt}</p>
          )}
        </div>
      );


      // Add text after image if exists
      if (textAfter) {
        const formattedText = formatInlineMarkdown(textAfter);
        elements.push(
          <p key={key++} className="text-gray-700 mb-3 leading-relaxed">
            {formattedText}
          </p>
        );
      }

      continue;
    }

    // Ordered list items like "1. text" or "1、text"
    const orderedMatch = line.match(/^(\s*)(\d+)[.\u3001]\s+(.*)$/);
    if (orderedMatch) {
      const [, indent, num, itemText] = orderedMatch;
      const indentLevel = Math.floor(indent.length / 2);
      const marginLeft = indentLevel * 1.5; // keep consistent with bullets
      const formattedText = formatInlineMarkdown(itemText);
      elements.push(
        <div
          key={key++}
          className="flex items-baseline mb-1.5"
          style={{ marginLeft: `${marginLeft}rem` }}
        >
          <span className="text-gray-500 w-5 mr-2 text-right">{num}.</span>
          <span className="text-gray-700" style={{ lineHeight: '24px' }}>{formattedText}</span>
        </div>
      );
      continue;
    }

    // Check for bullet points with indentation (for nested lists)
    const bulletMatch = line.match(/^(\s*)([-•])\s+(.*)$/);
    if (bulletMatch) {
      const [, indent, , bulletText] = bulletMatch;
      const indentLevel = Math.floor(indent.length / 2); // 2 spaces = 1 indent level
      const formattedText = formatInlineMarkdown(bulletText);
      
      // Calculate left margin based on indent level (each level = 1.5rem)
      const marginLeft = indentLevel * 1.5;
      
      elements.push(
        <div 
          key={key++} 
          className="flex items-baseline mb-1.5"
          style={{ marginLeft: `${marginLeft}rem` }}
        >
          <span className="text-primary-600 w-3 mr-2 text-center">•</span>
          <span className="text-gray-700" style={{ lineHeight: '24px' }}>{formattedText}</span>
        </div>
      );
      continue;
    }

    // Regular paragraph (use trimmed line for paragraphs)
    const formattedText = formatInlineMarkdown(trimmedLine);
    elements.push(
      <p key={key++} className="text-gray-700 mb-3 leading-relaxed">
        {formattedText}
      </p>
    );
  }

  return elements;
}

/**
 * Format inline markdown like **bold** and *italic*
 * @param {string} text - Text with inline markdown
 * @returns {JSX.Element|string} Formatted JSX or plain string
 */
function formatInlineMarkdown(text) {
  if (!text || typeof text !== 'string') return text;

  // Basic/stable: handle bold only for main views
  const boldRegex = /\*\*(.*?)\*\*/g;
  const parts = [];
  let lastIndex = 0;
  let match;
  let key = 0;

  while ((match = boldRegex.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    parts.push(<strong key={key++} className="font-semibold text-gray-900">{match[1]}</strong>);
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  if (parts.length === 0) return text;
  if (parts.length === 1 && typeof parts[0] === 'string') return parts[0];
  return <span>{parts}</span>;
}

// Enhanced parsing for Share Image rendering only
export function parseMarkdownForShare(text) {
  if (!text) return [];
  const lines = text.split('\n');
  const elements = [];
  let key = 0;

  const formatInlineAdvanced = (input) => {
    const boldRegex = /\*\*(.*?)\*\*/g;
    const italicRegex = /(?<!\*)\*(?!\s)(.*?)(?<!\s)\*(?!\*)/g;
    const codeRegex = /`([^`]+)`/g;

    const parts = [];
    let k = 0;

    // split by code spans first
    const codeSegments = [];
    let last = 0; let m;
    while ((m = codeRegex.exec(input)) !== null) {
      if (m.index > last) codeSegments.push({ type: 'text', value: input.slice(last, m.index) });
      codeSegments.push({ type: 'code', value: m[1] });
      last = m.index + m[0].length;
    }
    if (last < input.length) codeSegments.push({ type: 'text', value: input.slice(last) });

    codeSegments.forEach(seg => {
      if (seg.type === 'code') {
        parts.push(<code key={k++} className="px-1 py-0.5 rounded bg-gray-100 text-gray-800">{seg.value}</code>);
      } else {
        let idx = 0; let bm;
        while ((bm = boldRegex.exec(seg.value)) !== null) {
          if (bm.index > idx) parts.push(seg.value.slice(idx, bm.index));
          parts.push(<strong key={k++} className="font-semibold text-gray-900">{bm[1]}</strong>);
          idx = bm.index + bm[0].length;
        }
        if (idx < seg.value.length) parts.push(seg.value.slice(idx));
      }
    });

    // italics over string chunks
    for (let i = 0; i < parts.length; i++) {
      if (typeof parts[i] === 'string') {
        const s = parts[i];
        const chunks = []; let lastI = 0; let im;
        while ((im = italicRegex.exec(s)) !== null) {
          if (im.index > lastI) chunks.push(s.slice(lastI, im.index));
          chunks.push(<em key={k++} className="italic">{im[1]}</em>);
          lastI = im.index + im[0].length;
        }
        if (lastI < s.length) chunks.push(s.slice(lastI));
        if (chunks.length) parts[i] = <span key={k++}>{chunks}</span>;
      }
    }

    return <span>{parts.length ? parts : input}</span>;
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (!line.trim()) { elements.push(<div key={key++} className="h-3" />); continue; }

    // Ordered list items like "1. text" or "1、text"
    const orderedMatch = line.match(/^(\s*)(\d+)[.\u3001]\s+(.*)$/);
    if (orderedMatch) {
      const [, indent, num, itemText] = orderedMatch;
      const indentLevel = Math.floor(indent.length / 2);
      const marginLeft = indentLevel * 1.5;
      elements.push(
        <div key={key++} className="flex items-start mb-2" style={{ marginLeft: `${marginLeft}rem` }}>
          <span className="text-gray-500 mr-3 mt-0.5 w-6 text-right">{num}.</span>
          <span className="text-gray-700 leading-relaxed">{formatInlineAdvanced(itemText)}</span>
        </div>
      );
      continue;
    }

    const bulletMatch = line.match(/^(\s*)([-•])\s+(.*)$/);
    if (bulletMatch) {
      const [, indent, , bulletText] = bulletMatch;
      const indentLevel = Math.floor(indent.length / 2);
      const marginLeft = indentLevel * 1.5;
      elements.push(
        <div key={key++} className="flex items-start mb-2" style={{ marginLeft: `${marginLeft}rem` }}>
          <span className="text-primary-600 mr-3 mt-1">•</span>
          <span className="text-gray-700 leading-relaxed">{formatInlineAdvanced(bulletText)}</span>
        </div>
      );
      continue;
    }

    elements.push(
      <p key={key++} className="text-gray-700 mb-2.5" style={{ lineHeight: '24px' }}>{formatInlineAdvanced(line.trim())}</p>
    );
  }

  return elements;
}

/**
 * Check if text contains markdown formatting
 * @param {string} text - Text to check
 * @returns {boolean} True if text contains markdown
 */
export function hasMarkdownFormatting(text) {
  if (!text) return false;

  return (
    text.includes('**') ||
    text.includes('`') ||
    /(^|\n)\s*[•-]\s*/.test(text) ||
    /(^|\n)\s*\d+[.\u3001]\s+/.test(text) ||
    /(^|\n)\s*#{1,3}\s+/.test(text) ||
    /!\[.*?\]\(.*?\)/.test(text) // Check for markdown images
  );
}

/**
 * Strip markdown formatting from text for clean display in summaries
 * @param {string} text - Text with markdown formatting
 * @returns {string} Plain text with markdown removed
 */
export function stripMarkdownFormatting(text) {
  if (!text) return text;

  return text
    // Remove headers (### ## #)
    .replace(/^#{1,3}\s+/gm, '')
    // Remove bold (**text**)
    .replace(/\*\*(.*?)\*\*/g, '$1')
    // Remove italic (*text*)
    .replace(/(?<!\*)\*(?!\s)(.*?)(?<!\s)\*(?!\*)/g, '$1')
    // Remove code (`text`)
    .replace(/`([^`]+)`/g, '$1')
    // Remove bullet points (- text or • text)
    .replace(/^(\s*)([-•])\s+/gm, '$1')
    // Remove numbered lists (1. text or 1、text)
    .replace(/^(\s*)(\d+)[.\u3001]\s+/gm, '$1')
    // Remove section headers like **Key Arguments:**
    .replace(/\*\*(.*?):\*\*/g, '$1:')
    // Clean up multiple spaces and newlines
    .replace(/\s+/g, ' ')
    .trim();
}